export const TOURS = [
  {
    id: 'welcome',
    title: 'Show me around',
    minutes: 7,
    needsAccount: true,
    featured: true,
    summary: 'Your deal list, Unified, then the whole file: dates, Deal, Docs, and Shares. The hat on a deal starts this same walkthrough.',
    steps: [
      {
        route: '/dashboard',
        selector: '[data-tour="new-deal"]',
        title: 'Start a new file here',
        body: 'New Transaction opens a blank deal. You can name it and, if you want, start from a template that already has the usual dates.',
        skipIfPathPrefix: '/transaction/',
        skipIfPaths: ['/unified'],
      },
      {
        route: '/dashboard',
        selector: '[data-tour="templates-nav"]',
        title: 'Templates are reusable date lists',
        body: 'A template is a saved list of dates and tasks you can use on a new deal. On a larger screen it is in the top bar. On a phone, open the menu.',
        skipIfPathPrefix: '/transaction/',
        skipIfPaths: ['/unified'],
        fallback: 'On a phone, Templates is in the menu. Next continues.',
      },
      {
        route: ['/dashboard', '/unified'],
        selector: '[data-tour="unified-nav"]',
        altSelector: '[data-tour="templates-nav"]',
        title: 'Unified is every file at once',
        body: 'Dashboard is one deal at a time. Unified puts the dates from all your files on one list, so you can see what is due across every deal. Open a row to jump into that file.',
        skipIfPathPrefix: '/transaction/',
        fallback: 'On a phone, Unified is in the menu. Next continues.',
        nudge: 'unified-view',
      },
      {
        route: '/dashboard',
        selector: '[data-tour="deal-card"]',
        title: 'Open a deal to keep going',
        body: 'The rest of this walkthrough is inside a file. Open any deal. A bar stays on screen so you know you are still on the same tour.',
        waitForPathPrefix: '/transaction/',
        skipIfPathPrefix: '/transaction/',
        fallback: 'No deal yet. Make one when you are ready, then click the hat on that page.',
      },
      {
        route: '/transaction',
        selector: '[data-tour="room-timeline"]',
        altSelector: '[data-tour="page-timeline"]',
        dealRoom: 'timeline',
        title: 'Timeline is the dates',
        body: 'Each row is a date you track. A due date is that calendar day in Hawaii. It is not late until that Hawaii day ends. Relative dates, such as 11 days after Mutual Execution, update when the first date changes.',
      },
      {
        route: '/transaction',
        selector: '[data-tour="timeline-item"]',
        dealRoom: 'timeline',
        title: 'Weekends and holidays move the date',
        body: 'If a deadline lands on Saturday, Sunday, or a Hawaii holiday such as Kamehameha Day, the date moves. You do not count it by hand. Expand a row and use Edit to change how it is counted.',
        fallback: 'Open Timeline if you do not see a date row.',
      },
      {
        route: '/transaction',
        selector: '[data-tour="timeline-item"]',
        dealRoom: 'timeline',
        title: 'A later date can wait on an earlier one',
        body: 'Example: the seller has 5 days to deliver a disclosure. They deliver on day 3. The buyer then has 3 days from that delivery, not from the original due date.',
        fallback: 'Open Timeline if you do not see a date row.',
      },
      {
        route: '/transaction',
        selector: '[data-tour="room-deal"]',
        altSelector: '[data-tour="page-deal"]',
        dealRoom: 'summary',
        title: 'Deal is the facts',
        body: 'People, money, and the property live here. On this page, Library copies your card or a saved person onto a seat. That copy stays on the file.',
        hintDataset: 'tourHasContract',
        hintWhenPresent: 'This file can fill a Hawaii purchase form. Use Edit deal, then Contract mode. Type the answers. Make a PDF. It is saved in Docs.',
      },
      {
        route: '/transaction',
        selector: '[data-tour="room-docs"]',
        altSelector: '[data-tour="page-docs"]',
        dealRoom: 'documents',
        title: 'Docs holds the files',
        body: 'PDFs and other papers for this deal land here.',
        nudge: 'docs-pro',
      },
      {
        route: '/transaction',
        selector: '[data-tour="room-shares"]',
        altSelector: '[data-tour="page-shares"]',
        dealRoom: 'share',
        title: 'Shares is a link, not the whole file',
        body: 'You choose what a client, escrow, or lender can see. It does not email the whole file.',
        nudge: 'team-list',
      },
      {
        route: '/transaction',
        selector: '[data-tour="new-share"]',
        dealRoom: 'share',
        title: 'Make a link from here',
        body: 'New share lets you pick a role and what they see. Private notes stay off unless you turn them on.',
        fallback: 'New share is on this Shares page.',
      },
      {
        route: '/transaction',
        selector: '[data-tour="header-alerts"]',
        dealRoom: 'share',
        title: 'You still get reminders',
        body: 'The bell is your in-app alerts. Email reminders are in Settings, then Alerts.',
      },
    ],
  },
  {
    id: 'people-form',
    title: 'Your card and people',
    minutes: 3,
    needsAccount: true,
    summary: 'Fill your professional card in Library, then stamp it onto a deal from the Deal page.',
    steps: [
      {
        route: '/settings',
        selector: '[data-tour="library-tab"]',
        settingsTab: 'professional',
        keepPageVisible: true,
        title: 'Library is here',
        body: 'This is your professional card: name, license, and brokerage. Below it, save escrow officers and other people you reuse. The list lives on your account, not on one deal.',
        nudge: 'team-list',
      },
      {
        route: '/settings',
        selector: '[data-tour="library-card"]',
        settingsTab: 'professional',
        keepPageVisible: true,
        title: 'Stamp your card onto a file',
        body: 'Open a deal. Go to Deal. In the people section, click Library. Pick your card, then Buyer Agent or Seller Agent. Name, license, and brokerage copy onto that file. Later edits here do not change deals you already stamped.',
      },
      {
        route: '/settings',
        selector: '[data-tour="library-people"]',
        settingsTab: 'professional',
        keepPageVisible: true,
        title: 'Other people stamp the same way',
        body: 'Save an escrow officer or lender here. On a deal, the same Library button copies them onto a seat. It is a snapshot of that day, not a live link back to this list.',
        fallback: 'Saved people is the list under your card on this Library page.',
      },
    ],
  },
  {
    id: 'tax-tools',
    title: 'Hawaii tax tools',
    minutes: 6,
    needsAccount: false,
    summary: 'Taxes with VA funding fees, loan math, holidays, TMK formats, and form links. No account needed.',
    steps: [
      {
        route: '/tools',
        selector: '[data-tour="tools-taxes"]',
        toolsTab: 'calculators',
        keepPageVisible: true,
        title: 'Taxes and a net sheet',
        body: 'Property tax, conveyance tax, HARPTA, and FIRPTA sit on this page. The net sheet estimates what a buyer or seller nets at closing. For a VA loan it can add the VA funding fee, using official rates we keep current from va.gov. If you are signed in, you can use the numbers on a deal.',
      },
      {
        route: '/tools',
        selector: '[data-tour="tools-loan"]',
        toolsTab: 'loan',
        keepPageVisible: true,
        title: 'Loan payment',
        body: 'Estimate a monthly payment. Change price, rate, and term, watch the balance drop, and compare a fixed rate with one that adjusts later. Tap Use average rate to fill a typical interest rate so you are not guessing.',
      },
      {
        route: '/tools',
        selector: '[data-tour="tools-holidays"]',
        toolsTab: 'holidays',
        keepPageVisible: true,
        title: 'Hawaii holidays',
        body: 'Federal and Hawaii holidays are listed here, including days such as Kamehameha Day. Deadlines on a deal skip Saturday, Sunday, and these days. You can also count days from a start date and skip those same days, which helps when a deadline is not on a deal yet.',
      },
      {
        route: '/tools',
        selector: '[data-tour="tools-tmk"]',
        toolsTab: 'tmk-converter',
        keepPageVisible: true,
        title: 'Tax map key',
        body: 'A TMK is the tax map key for a Hawaii property. Offices write the same parcel in more than one format. Paste one here to see the island, zone, and the other spellings, including the extra digits for a condo.',
      },
      {
        route: '/tools',
        selector: '[data-tour="tools-pdfformstorm"]',
        keepPageVisible: true,
        title: 'Fill a PDF',
        body: 'This opens a separate site for filling PDF forms you already have. Use it when the paper is not built into a deal, so you are not filling boxes by hand in a printer preview.',
      },
      {
        route: '/tools',
        selector: '[data-tour="tools-squaredeal"]',
        keepPageVisible: true,
        title: 'Hawaii purchase forms',
        body: 'This opens the Hawaii purchase forms in a new window when you want the blank paper, the clause list, or a quick look at the current form before you fill one on a deal.',
      },
    ],
  },
];

const TOUR_ALIASES = {
  'getting-started': 'welcome',
  'first-deal': 'welcome',
  'a-deal': 'welcome',
  'hawaii-dates': 'welcome',
  'stacked-dates': 'welcome',
  'remind-share': 'welcome',
  'tools-team': 'tax-tools',
};

export function getTour(tourId) {
  const resolved = TOUR_ALIASES[tourId] || tourId;
  return TOURS.find((tour) => tour.id === resolved) || null;
}

function clampStepIndex(value, length) {
  if (!Number.isInteger(value) || length <= 0) return 0;
  return Math.max(0, Math.min(value, length - 1));
}

export function resolveStoredTour(raw, pathname = '') {
  if (!raw) return null;
  const tour = getTour(raw.id);
  if (!tour) return null;
  const step = typeof raw.step === 'number' ? raw.step : 0;
  if (step < 0 || step >= tour.steps.length) return null;
  const originPath = typeof raw.originPath === 'string' ? raw.originPath : pathname;
  const originDealRoom = typeof raw.originDealRoom === 'string' ? raw.originDealRoom : '';
  const originStep = Number.isInteger(raw.originStep)
    ? clampStepIndex(raw.originStep, tour.steps.length)
    : firstStepIndex(tour, originPath, originDealRoom);
  return {
    id: tour.id,
    step,
    originStep,
    originPath,
    originDealRoom,
  };
}

export function tourSessionAt(tourId, step, origin = {}) {
  return {
    id: tourId,
    step,
    originStep: Number.isInteger(origin.originStep) ? origin.originStep : step,
    originPath: typeof origin.originPath === 'string' ? origin.originPath : '',
    originDealRoom: typeof origin.originDealRoom === 'string' ? origin.originDealRoom : '',
  };
}

export function skipPathForOrigin(origin, pathname = '') {
  if (typeof origin?.originPath === 'string' && origin.originPath) return origin.originPath;
  return pathname;
}

export function shouldSkipForOrigin(step, origin, pathname = '') {
  const skipPath = skipPathForOrigin(origin, pathname);
  if (shouldSkipStep(step, skipPath)) return true;
  return shouldSkipEarlierDealRoom(step, origin?.originDealRoom || '');
}

export function nextTour(tourId) {
  const resolved = TOUR_ALIASES[tourId] || tourId;
  const index = TOURS.findIndex((tour) => tour.id === resolved);
  if (index < 0 || index >= TOURS.length - 1) return null;
  return TOURS[index + 1];
}

export function pathMatchesRoute(pathname, route) {
  if (!route) return true;
  if (Array.isArray(route)) return route.some((entry) => pathMatchesRoute(pathname, entry));
  if (route === '/transaction') return pathname.startsWith('/transaction/');
  if (route === '/settings') return pathname === '/settings' || pathname.startsWith('/settings/');
  return pathname === route || pathname.startsWith(`${route}/`);
}

export const DEAL_ROOM_ORDER = ['timeline', 'summary', 'documents', 'share'];

export function normalizeTourDealRoom(pageView) {
  if (!pageView) return '';
  if (pageView === 'documents') return 'documents';
  if (pageView === 'share') return 'share';
  if (pageView === 'timeline') return 'timeline';
  if (pageView === 'edit' || pageView === 'netsheet' || pageView === 'partial') return 'edit';
  if (pageView === 'tasks') return 'tasks';
  return 'summary';
}

/** Room to use when starting a tour on a deal. Edit counts as Deal. Tasks counts as Timeline. */
export function dealRoomForTourStart(pageView) {
  const room = normalizeTourDealRoom(pageView);
  if (room === 'edit') return 'summary';
  if (room === 'tasks') return 'timeline';
  return room;
}

export function readCurrentDealRoom() {
  if (typeof document === 'undefined') return '';
  return document.documentElement?.dataset?.dealRoom || '';
}

export function shouldSkipEarlierDealRoom(step, dealRoom) {
  if (!dealRoom || !step?.dealRoom) return false;
  const from = DEAL_ROOM_ORDER.indexOf(step.dealRoom);
  const at = DEAL_ROOM_ORDER.indexOf(dealRoom);
  if (from < 0 || at < 0) return false;
  return from < at;
}

export function firstStepIndex(tour, pathname, dealRoom = '') {
  if (!tour) return 0;
  let index = 0;
  while (index < tour.steps.length) {
    const step = tour.steps[index];
    if (shouldSkipStep(step, pathname) || shouldSkipEarlierDealRoom(step, dealRoom)) {
      index += 1;
      continue;
    }
    break;
  }
  if (index >= tour.steps.length) return Math.max(0, tour.steps.length - 1);
  return index;
}

export function visibleStepMeta(tour, stepIndex, pathname, dealRoom = '', origin = null) {
  if (!tour) return { current: 1, total: 1, isFirst: true, isLast: true };
  const frozen = origin && typeof origin === 'object' ? origin : {};
  const originStep = Number.isInteger(frozen.originStep) ? frozen.originStep : 0;
  const originPath = frozen.originPath !== undefined && frozen.originPath !== null
    ? frozen.originPath
    : pathname;
  const originDealRoom = frozen.originDealRoom !== undefined && frozen.originDealRoom !== null
    ? frozen.originDealRoom
    : dealRoom;
  const vis = [];
  for (let index = originStep; index < tour.steps.length; index += 1) {
    const step = tour.steps[index];
    if (shouldSkipStep(step, originPath) || shouldSkipEarlierDealRoom(step, originDealRoom)) continue;
    vis.push(index);
  }
  if (vis.length === 0) return { current: 1, total: 1, isFirst: true, isLast: true };
  let pos = vis.indexOf(stepIndex);
  if (pos < 0) {
    pos = vis.findIndex((index) => index > stepIndex);
    if (pos < 0) pos = vis.length - 1;
  }
  return {
    current: pos + 1,
    total: vis.length,
    isFirst: pos <= 0,
    isLast: pos >= vis.length - 1,
  };
}

export function shouldSkipStep(step, pathname) {
  if (step?.skipIfPathPrefix && pathname.startsWith(step.skipIfPathPrefix)) return true;
  if (Array.isArray(step?.skipIfPaths) && step.skipIfPaths.some((prefix) => (
    pathname === prefix || pathname.startsWith(`${prefix}/`)
  ))) return true;
  if (step?.requireDataset) {
    if (typeof document === 'undefined') return false;
    return document.documentElement?.dataset?.[step.requireDataset] !== '1';
  }
  return false;
}

export function stepWaitsForPath(step, pathname) {
  if (!step?.waitForPathPrefix) return false;
  return !pathname.startsWith(step.waitForPathPrefix);
}

export function shouldAdvanceWaitStep(step, pathname) {
  return Boolean(step?.waitForPathPrefix && pathname.startsWith(step.waitForPathPrefix));
}

/**
 * Which tour the hat icon starts on this screen.
 * Returns null on screens that should not show a page tour (Learn hub, shared links).
 */
export function tourIdForPath(pathname) {
  if (!pathname) return 'welcome';
  if (pathname === '/learn' || pathname.startsWith('/learn/')) return null;
  if (pathname.startsWith('/shared/')) return null;
  if (pathname === '/tools' || pathname.startsWith('/tools/')) return 'tax-tools';
  if (pathname === '/settings' || pathname.startsWith('/settings/')) return 'people-form';
  if (pathname.startsWith('/transaction/')) return 'welcome';
  return 'welcome';
}
